import React from "react";
import Beam from "./Beam";
import { useConfigurationOptionsContext } from "../../../../../app/providers/DoorOptionsProvider";
import { DoorGlassMaterial } from "shared/utils/door";
import { lockSettings } from "shared/utils/doorConfigurations";
import { isDeBlaakCode } from "entities/configurator/model/layoutMeta";
import {
  defaultDoorWidth,
  defaultLockHandleYPosition,
  defaultLockHeight,
  defaultSideFrameWidth,
} from "shared/utils/doorSceneConstants";
import { getGeometryBounds2D, getOpeningAnchoredX } from "./openingGeometry";

interface LockProps extends lockSettings {
  doorNodes: any;
  materials: DoorGlassMaterial;
  secondary?: boolean;
  reverse?: boolean;
  type: string;
  index: number;
}

const Lock: React.FC<LockProps> = ({
  doorNodes,
  materials,
  secondary,
  reverse,
  type,
  index,
  ...props
}: LockProps) => {
  const {
    layout,
    getConfigurationAnchorBeams,
    getConfigurationWidth,
    getConfigurationColor,
  } = useConfigurationOptionsContext();

  const anchorBeams = getConfigurationAnchorBeams();
  const topBeam = anchorBeams[0] ?? null;
  const bottomBeam = anchorBeams[1] ?? null;
  const topBeamY = (topBeam?.height ?? 1280) / 1000;
  const bottomBeamY = (bottomBeam?.height ?? 640) / 1000;
  const width = getConfigurationWidth(index);
  const glassBounds = getGeometryBounds2D(doorNodes.glass.geometry);
  const defaultLockPlateX = reverse
    ? (defaultDoorWidth / 2 - defaultSideFrameWidth * 2) / 1000
    : -(defaultDoorWidth / 2 - defaultSideFrameWidth * 2) / 1000;
  const lockPlatePositionX = getOpeningAnchoredX({
    glassBounds,
    width,
    defaultX: defaultLockPlateX,
    reverse,
  });
  const lockPlatePositionY = bottomBeamY + (topBeamY - bottomBeamY) / 2;
  const lockPlateScaleY =
    (topBeamY * 1000 - bottomBeamY * 1000) / defaultLockHeight;
  const usesFixedLockHandleHeight =
    isDeBlaakCode(layout?.code) || layout?.code === "eigenwijs";
  const lockMisc0PositionY = usesFixedLockHandleHeight
    ? defaultLockHandleYPosition
    : lockPlatePositionY;
  const lockMisc1PositionY = usesFixedLockHandleHeight
    ? defaultLockHandleYPosition - 0.06
    : lockPlatePositionY - 0.06;

  return (
    <group rotation-y={props.rotateLock ?? 0}>
      {/* Handle top beam */}
      <Beam
        id={topBeam?.id ?? null}
        key={`lock-beam-0`}
        doorNodes={doorNodes}
        materials={materials}
        width={width}
        height={topBeamY}
      />
      {/* Handle bottom beam */}
      <Beam
        id={bottomBeam?.id ?? null}
        key={`lock-beam-1`}
        doorNodes={doorNodes}
        materials={materials}
        width={width}
        height={bottomBeamY}
      />
      {props?.handleSlotkastBody?.enabled !== false && (
        <mesh
          geometry={doorNodes["handle_slotkast_body"].geometry}
          material={materials[getConfigurationColor()]}
          position={[lockPlatePositionX, lockPlatePositionY, 0]}
          scale={[1, lockPlateScaleY, 1]}
        ></mesh>
      )}
      {!secondary && (
        <>
          <mesh
            geometry={doorNodes["handle_slotkast_misc_0"].geometry}
            material={materials[getConfigurationColor()]}
            rotation-y={props?.rotateHandleSlotkast ?? (reverse ? 0 : Math.PI)}
            position={[
              reverse ? lockPlatePositionX - 0.04 : lockPlatePositionX + 0.04,
              lockMisc0PositionY,
              0,
            ]}
          ></mesh>
          {(type.includes("toiletbeslag") || type.includes("cilinderslot")) && (
            <mesh
              geometry={doorNodes["handle_slotkast_misc_1"].geometry}
              material={materials[getConfigurationColor()]}
              position={[lockPlatePositionX, lockMisc1PositionY, 0]}
            ></mesh>
          )}
        </>
      )}
    </group>
  );
};

export default Lock;
